import { ItemType } from "@core/FileStructue/Item/ItemType";
import type { ClientArticleProps } from "@core/SitePresenter/SitePresenter";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import useWatch from "@core-ui/hooks/useWatch";
import { useArticlePropsStore } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import BugsnagTrigger from "@ext/bugsnag/components/BugsnagTrigger";
import { NotificationSettingsButton } from "@ext/enterprise/components/NotificationSettingsButton";
import { useIsEnterprise } from "@ext/enterprise/utils/useIsEnterprise";
import { useIsAnyGes } from "@ext/enterpriseCommon/hooks/useIsAnyGes";
import { ArticleRevisionTrigger } from "@ext/git/actions/Revisions/components/ArticleRevisionTrigger";
import { DeleteItemTrigger } from "@ext/item/actions/DeleteArticleTrigger";
import { SearchInScopeTrigger } from "@ext/item/actions/SearchInScopeTrigger";
import t from "@ext/localization/locale/translate";
import {
	isReadOnlyGroup,
	isUnnestableArticle,
	useNavigationTreeStore,
} from "@ext/navigation/catalog/SidebarNavigation/store/navigationTreeStore";
import { DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSkeletonItem } from "@ui-kit/Dropdown";
import React, { type Dispatch, type SetStateAction, useCallback, useState } from "react";
import type { Environment } from "../../../app/resolveModule/env";
import ExportToDocxOrPdf from "../../components/Actions/ExportToDocxOrPdf";
import ShowInExplorer from "../../components/Actions/ShowInExplorer";
import { usePlatform } from "../../ui-logic/hooks/usePlatform";
import ArticleMoveAction from "../article/actions/move/ArticleMoveAction";
import ShareAction from "../catalog/actions/share/components/ShareAction";
import { ArticleFavoriteSettingsButton } from "../enterprise/components/ArticleFavoriteSettingsButton";
import EnterpriseCheckStyleGuide from "../enterprise/components/EnterpriseCheckStyleGuide";
import type { CategoryLink, ItemLink } from "../navigation/NavigationLinks";
import ArticleLinks from "../properties/components/Helpers/ArticleLinks";
import TemplateItemList from "../templates/components/TemplateItemList";
import PropsEditorTrigger from "./actions/propsEditor/components/PropsEditorTrigger";

interface EditMenuProps {
	itemLink: ItemLink;
	setItemLink: Dispatch<SetStateAction<ItemLink>>;
	onExpandedChange?: (itemPath: string, expanded: boolean) => void;
	onOpen?: () => void;
	onClose?: () => void;
}

const HeaderLabel: React.FC = () => (
	<DropdownMenuLabel className="text-primary-fg">{t("article.actions.title")}</DropdownMenuLabel>
);

const components: Record<Environment, (props: EditMenuProps) => React.ReactNode> = {
	web: (props) => <AnyEditorEditMenu {...props} />,
	tauri: (props) => <AnyEditorEditMenu {...props} />,
	next: (props) => <ReadonlyEditMenu {...props} />,
	static: (props) => <ReadonlyEditMenu {...props} />,
	cli: (props) => <ReadonlyEditMenu {...props} />,
	docportal: (props) => <ReadonlyEditMenu {...props} />,
	test: () => null,
};

const EditMenu = React.memo((props: EditMenuProps) => {
	const { environment } = usePlatform();
	return components[environment](props);
});

export default EditMenu;

function useEditMenuItemProps(itemLink: ItemLink | CategoryLink) {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const [fetchedProps, setFetchedProps] = useState<ClientArticleProps>(null);
	const [loadedPath, setLoadedPath] = useState<string>(null);
	const openedArticleProps = useArticlePropsStore((state) =>
		state?.data.ref?.path === itemLink.ref.path ? state.data : null,
	);

	const setItemPropsData = useCallback(async () => {
		const path = itemLink.ref.path;
		if (!path) return;
		try {
			const response = await FetchService.fetch(apiUrlCreator.getItemProps(path));
			if (response.ok) setFetchedProps((await response.json()) as ClientArticleProps);
		} finally {
			setLoadedPath(path);
		}
	}, [itemLink?.ref?.path]);

	useWatch(() => {
		void setItemPropsData();
	}, [itemLink?.ref?.path]);

	const isFetched = loadedPath === itemLink.ref.path;
	const itemProps = isFetched ? fetchedProps : openedArticleProps;
	const isLinkToValidArticle = itemProps && !itemProps.errorCode;
	const isCategory = itemLink.type === ItemType.category && (itemLink as CategoryLink).items.length > 0;
	const isCurrentItem = itemProps?.ref.path === itemLink.ref.path;
	const isLoading = !!itemLink.ref.path && !isFetched && !openedArticleProps;

	return { itemProps, setItemPropsData, isLoading, isLinkToValidArticle, isCategory, isCurrentItem };
}

const ItemPropsActionsSkeleton = () => (
	<>
		<DropdownMenuSkeletonItem labelClassName="w-40" />
		<DropdownMenuSkeletonItem labelClassName="w-28" />
		<DropdownMenuSkeletonItem labelClassName="w-36" />
		<DropdownMenuSeparator />
		<DropdownMenuSkeletonItem labelClassName="w-32" />
		<DropdownMenuSkeletonItem labelClassName="w-24" />
		<DropdownMenuSeparator />
		<DropdownMenuSkeletonItem labelClassName="w-36" />
	</>
);

const ReadonlyEditMenu = ({ itemLink }: EditMenuProps) => {
	const isCategory = itemLink.type === ItemType.category && (itemLink as CategoryLink).items.length > 0;

	if (itemLink.options?.isHasErrorCode) return null;

	return (
		<>
			<HeaderLabel />
			<DropdownMenuSeparator />
			<SearchInScopeTrigger isCategory={isCategory} itemLink={itemLink} />
			<ArticleFavoriteSettingsButton itemLinkPath={itemLink.ref.path} />
			<DropdownMenuSeparator />
			<ExportToDocxOrPdf
				fileName={itemLink.fileName}
				isCategory={isCategory}
				itemRefPath={itemLink.ref.path}
				title={itemLink.title}
			/>
		</>
	);
};

const AnyEditorEditMenu = (props: EditMenuProps) => {
	const isReadOnly = PageDataContextService.value.conf.isReadOnly;
	const limitedActions = useCatalogPropsStore((state) => !!state.data?.behavior.limitedActions);
	if (isReadOnly) return <ReadonlyEditMenu {...props} />;
	// Its items are only deleted, and their trigger deletes them itself: see `useIsDeleteOnlyItem`.
	if (limitedActions) return null;
	return <EditorEditMenu {...props} />;
};

/** A delete button in place of a menu of one item; `ReadonlyEditMenu` (read-only catalog, DocPortal) keeps its menu. */
export const useIsDeleteOnlyItem = (): boolean => {
	const { environment } = usePlatform();
	const isReadOnly = PageDataContextService.value.conf.isReadOnly;
	const limitedActions = useCatalogPropsStore((state) => !!state.data?.behavior.limitedActions);
	const isEditor = environment === "web" || environment === "tauri";
	return isEditor && !isReadOnly && limitedActions;
};

export const useHasItemActions = (itemLink: ItemLink | null, topLevelLinks: ItemLink[]): boolean => {
	const behavior = useCatalogPropsStore((state) => state.data?.behavior);
	const isTopLevelGroup =
		itemLink?.type === ItemType.category && topLevelLinks.some((link) => link.ref.path === itemLink.ref.path);
	return !(behavior?.limitedActions && behavior.readOnlyTopCategories && isTopLevelGroup);
};

const EditorEditMenu = ({ itemLink, setItemLink, onExpandedChange }: EditMenuProps) => {
	const { itemProps, setItemPropsData, isLoading, isCategory, isLinkToValidArticle, isCurrentItem } =
		useEditMenuItemProps(itemLink);

	const isEnterprise = useIsEnterprise();
	const isAnyGes = useIsAnyGes();
	// Same rule as the catalog's share action: an unpublished catalog has nothing to link to.
	const hasSource = useCatalogPropsStore((state) => !!state.data?.sourceName);
	const isReadOnlyItem = useNavigationTreeStore((s) => isReadOnlyGroup(s, itemLink.ref.path));
	const isUnnestable = useNavigationTreeStore((s) => isUnnestableArticle(s, itemLink.ref.path));
	const canChange = isLinkToValidArticle && !isReadOnlyItem;

	return (
		<>
			<HeaderLabel />
			{isLoading ? (
				<ItemPropsActionsSkeleton />
			) : (
				<>
					{canChange && (
						<>
							<PropsEditorTrigger
								isCategory={isCategory}
								item={itemProps}
								itemLink={itemLink}
								onExpandedChange={onExpandedChange}
								setItemLink={setItemLink}
								setItemPropsData={setItemPropsData}
							/>
							{isCurrentItem && <TemplateItemList itemRefPath={itemLink.ref.path} />}
						</>
					)}
					{!isReadOnlyItem && !isUnnestable && (
						<ArticleMoveAction articlePath={itemProps?.ref?.path} itemLink={itemLink} />
					)}
					{isLinkToValidArticle && <ArticleFavoriteSettingsButton itemLinkPath={itemLink.ref.path} />}
					{isLinkToValidArticle && <SearchInScopeTrigger isCategory={isCategory} itemLink={itemLink} />}
					<DropdownMenuSeparator />
					{isLinkToValidArticle && (
						<>
							{hasSource && <ShareAction copyOnly={isAnyGes} isArticle path={itemLink.pathname} />}
							<ArticleLinks itemRefPath={itemLink.ref.path} />
							<DropdownMenuSeparator />
						</>
					)}
					{isLinkToValidArticle && isCurrentItem && <EnterpriseCheckStyleGuide />}
					{isLinkToValidArticle && <ArticleRevisionTrigger itemLink={itemLink} />}
					<DropdownMenuSeparator />
					{isLinkToValidArticle && (
						<ExportToDocxOrPdf
							fileName={itemProps.fileName}
							isCategory={isCategory}
							itemRefPath={itemLink.ref.path}
							title={itemLink.title}
						/>
					)}
				</>
			)}
			{isEnterprise && (
				<>
					<DropdownMenuSeparator />
					<NotificationSettingsButton itemRefPath={itemLink.ref.path} />
				</>
			)}
			<ShowInExplorer item={itemLink} />
			<DropdownMenuSeparator />
			<BugsnagTrigger itemLogicPath={itemLink.ref.path} />
			{!isReadOnlyItem && (
				<>
					<DropdownMenuSeparator />
					<DeleteItemTrigger itemLink={itemLink} />
				</>
			)}
		</>
	);
};
